// src/pages/SettingsPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Settings,
  Users,
  Music,
  Calendar,
  Mail,
  Palette,
  HardDrive,
  FileText,
  Copy,
  FileSearch,
  Building2,
  ArrowLeft,
  UserPlus
} from 'lucide-react';
import Navigation from '../components/Navigation/Navigation';
import { PerformanceTypeColorSettings } from '../components/settings/PerformanceTypeColorSettings';
import { PerformanceTypeSettings } from '../components/settings/PerformanceTypeSettings';
import { DatabaseBackup } from '../components/settings/DatabaseBackup';
import { FileManagementSettings } from '../components/settings/FileManagementSettings';
import { ChurchesSettings } from '../components/settings/ChurchesSettings';
import { InviteManagement } from '../components/settings/InviteManagement';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import './SettingsPage.css';

interface SettingItem {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  path?: string;
  component?: React.ReactNode;
  requiresAdmin?: boolean;
  comingSoon?: boolean;
}

const SettingsPage: React.FC = () => {
  const [availablePerformanceTypes, setAvailablePerformanceTypes] = useState<string[]>([]);
  const [selectedItem, setSelectedItem] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role_name === 'admin';

  // Check if we came from a direct link to a settings page
  useEffect(() => {
    const pathMap: { [key: string]: string } = {
      '/admin/service-music-templates': 'service-templates',
      '/admin/liturgical-years': 'liturgical-years',
      '/admin/email-templates': 'email-templates',
      '/admin/duplicates': 'duplicates',
      '/admin/missing-files': 'missing-files',
      '/admin/churches': 'churches',
      '/settings/service-music-templates': 'service-templates',
      '/settings/liturgical-years': 'liturgical-years',
      '/settings/email-templates': 'email-templates',
      '/settings/duplicates': 'duplicates',
      '/settings/missing-files': 'missing-files',
      '/settings/churches': 'churches',
    };

    const currentPath = location.pathname;
    if (pathMap[currentPath]) {
      setSelectedItem(pathMap[currentPath]);
    }
  }, [location.pathname]);

  // Fetch performance types for color settings
  useEffect(() => {
    const fetchPerformanceTypes = async () => {
      try {
        const searchTerms = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'];
        const allTypes = new Set<string>();

        for (const term of searchTerms) {
          try {
            const response = await apiClient.get(`/song-selections/search-performance-types?term=${term}&limit=50`);
            const types = response.data.performanceTypes || [];
            types.forEach((type: string) => allTypes.add(type));
          } catch (error) {
            console.warn(`Failed to fetch performance types for term "${term}":`, error);
          }
        }

        const uniqueTypes = Array.from(allTypes).sort();
        setAvailablePerformanceTypes(uniqueTypes);

        if (uniqueTypes.length === 0) {
          setAvailablePerformanceTypes(['Congregational Hymn', 'Choir Anthem', 'Solo', 'Instrumental']);
        }
      } catch (error) {
        console.warn('Could not fetch performance types:', error);
        setAvailablePerformanceTypes(['Congregational Hymn', 'Choir Anthem', 'Solo', 'Instrumental']);
      }
    };

    fetchPerformanceTypes();
  }, []);

  // All settings items in a flat array
  const settingsItems: SettingItem[] = [
    ...(user?.username === 'jackbarben' ? [{ id: 'music-reflections', title: 'Music reflections', description: 'Manage weekly reflection emails, subscribers, and your private results', icon: <Music size={28} />, path: '/admin/music-reflections', requiresAdmin: true }] : []),
    { id: 'catalog-review', title: 'Catalog review', description: 'Answer the temporary song catalog questions and save decisions for implementation', icon: <FileSearch size={28} />, path: '/admin/catalog-review', requiresAdmin: true },
    { id: 'history-review', title: 'Old selections', description: 'Match song titles from past churches to the library before they are imported', icon: <FileSearch size={28} />, path: '/admin/history-review', requiresAdmin: true },
    {
      id: 'churches',
      title: 'Churches',
      description: 'Manage your churches and select the current church',
      icon: <Building2 size={28} />,
      component: <ChurchesSettings />,
    },
    {
      id: 'liturgical-years',
      title: 'Liturgical Year Setup',
      description: 'Manage liturgical years and calendar services',
      icon: <Calendar size={28} />,
      path: '/admin/liturgical-years',
      requiresAdmin: true,
    },
    {
      id: 'service-templates',
      title: 'Service Music Templates',
      description: 'Create and manage liturgical season templates',
      icon: <FileText size={28} />,
      path: '/admin/service-music-templates',
      requiresAdmin: true,
    },
    {
      id: 'church-management',
      title: 'Church Management (Admin)',
      description: 'Configure service definitions and advanced church settings',
      icon: <Building2 size={28} />,
      path: '/admin/churches',
      requiresAdmin: true,
    },
    {
      id: 'duplicates',
      title: 'Remove Duplicates',
      description: 'Find and manage duplicate song entries',
      icon: <Copy size={28} />,
      path: '/admin/duplicates',
      requiresAdmin: true,
    },
    {
      id: 'performance-types',
      title: 'Performance Types',
      description: 'Define and manage custom performance types for filtering',
      icon: <Music size={28} />,
      component: <PerformanceTypeSettings />,
      requiresAdmin: true,
    },
    {
      id: 'colors',
      title: 'Performance Type Colors',
      description: 'Customize colors for different performance types',
      icon: <Palette size={28} />,
      component: <PerformanceTypeColorSettings availablePerformanceTypes={availablePerformanceTypes} />,
    },
    {
      id: 'file-management',
      title: 'File Management',
      description: 'Rename and organize music files with canonical naming',
      icon: <FileText size={28} />,
      component: <FileManagementSettings />,
      requiresAdmin: true,
    },
    {
      id: 'missing-files',
      title: 'Missing Files for Sunday',
      description: 'Check which music files are missing for upcoming services',
      icon: <FileSearch size={28} />,
      path: '/admin/missing-files',
    },
    {
      id: 'backup',
      title: 'Database Backup',
      description: 'Backup and restore your database',
      icon: <HardDrive size={28} />,
      component: <DatabaseBackup />,
    },
    {
      id: 'email-templates',
      title: 'Email Templates',
      description: 'Configure automated music distribution emails',
      icon: <Mail size={28} />,
      path: '/admin/email-templates',
      requiresAdmin: true,
    },
    {
      id: 'invite-users',
      title: 'Invite Users',
      description: 'Generate and manage invite codes for new users',
      icon: <UserPlus size={28} />,
      component: <InviteManagement />,
      requiresAdmin: true,
    },
    {
      id: 'users',
      title: 'User Management',
      description: 'Manage user accounts and permissions',
      icon: <Users size={28} />,
      comingSoon: true,
      requiresAdmin: true,
    },
  ];

  // Filter items based on user role
  const filteredItems = settingsItems.filter(item => !item.requiresAdmin || isAdmin);

  const handleItemClick = (item: SettingItem) => {
    if (item.comingSoon) return;

    if (item.path) {
      navigate(item.path);
    } else {
      setSelectedItem(item.id);
    }
  };

  const handleBackClick = () => {
    setSelectedItem('');
  };

  // Find current item for detail view
  const currentItem = filteredItems.find(item => item.id === selectedItem);

  return (
    <>
      <Navigation />
      <div className="settings-page">
        <div className="settings-header">
          <Settings size={28} />
          <h1>Settings</h1>
        </div>

        {selectedItem && currentItem?.component ? (
          // Detail view with back button
          <div className="settings-detail">
            <button className="back-button" onClick={handleBackClick}>
              <ArrowLeft size={18} />
              <span>Back to Settings</span>
            </button>
            <h2 className="detail-title">{currentItem.title}</h2>
            <div className="detail-content">
              {currentItem.component}
            </div>
          </div>
        ) : (
          // Grid view
          <div className="settings-grid">
            {filteredItems.map(item => (
              <button
                key={item.id}
                className={`settings-tile ${item.comingSoon ? 'disabled' : ''}`}
                onClick={() => handleItemClick(item)}
                disabled={item.comingSoon}
              >
                <div className="tile-icon">{item.icon}</div>
                <div className="tile-title">{item.title}</div>
                {item.comingSoon && (
                  <div className="tile-badge">Coming Soon</div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default SettingsPage;

